import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { UserReportConfig } from './components/my-reports/user-report-config.model';
import { Observable } from 'rxjs';
import { ScheduledUserReport } from './components/my-reports/scheduled-user-report.model';
import { HttpUtils } from '../../shared/utils/http-utils';
import { ByDayReport, UnitByDayReportData, WidgetSizeByDayReportData, WinRateReportData } from './components/presets/report-preset.model';
import { Router } from '@angular/router';
import { ReportRequest } from '../shared/domain/report/report-request.model';
import { SummedMasterReport } from '../shared/domain/report/summed-master-report.model';

@Injectable()
export class ReportBuilderService {
  config: UserReportConfig;
  reportRequest: ReportRequest;

  constructor(private http: HttpClient, private router: Router) {
  }

  navigateToRbWithConfig(reportRequest: ReportRequest, newTab = false) {
    if (newTab) {
      window.open(`/#/reports/builder?config=${JSON.stringify(reportRequest)}`, '_blank').focus();
    } else {
      this.reportRequest = reportRequest;
      this.router.navigateByUrl('/reports/builder');
    }
  }

  getUserConfigs(): Observable<UserReportConfig[]> {
    return this.http.get<UserReportConfig[]>(`${environment.serviceUrl}/ad-stats/user-report-configs`);
  }

  createUserConfig(config: UserReportConfig): Observable<UserReportConfig> {
    return this.http.post<UserReportConfig>(`${environment.serviceUrl}/ad-stats/user-report-configs`, config);
  }

  updateUserConfig(config: UserReportConfig): Observable<UserReportConfig> {
    return this.http.put<UserReportConfig>(`${environment.serviceUrl}/ad-stats/user-report-configs/${config.id}`, config);
  }

  deleteUserConfig(config: UserReportConfig): Observable<UserReportConfig> {
    return this.http.delete<UserReportConfig>(`${environment.serviceUrl}/ad-stats/user-report-configs/${config.id}`);
  }

  getSharedConfigs(): Observable<UserReportConfig[]> {
    return this.http.get<UserReportConfig[]>(`${environment.serviceUrl}/ad-stats/user-report-configs/shared`);
  }

  scheduledUserConfigsGet(): Observable<ScheduledUserReport[]> {
    return this.http.get<ScheduledUserReport[]>(`${environment.serviceUrl}/ad-stats/scheduled-user-reports`);
  }

  scheduledUserConfigsPost(c: ScheduledUserReport, config: UserReportConfig): Observable<ScheduledUserReport> {
    return this.http.post<ScheduledUserReport>(`${environment.serviceUrl}/ad-stats/scheduled-user-reports/${config.id}`, c);
  }

  scheduledUserConfigsPut(c: ScheduledUserReport): Observable<ScheduledUserReport> {
    return this.http.put<ScheduledUserReport>(`${environment.serviceUrl}/ad-stats/scheduled-user-reports/${c.id}`, c);
  }

  scheduledUserConfigsDelete(c: ScheduledUserReport): Observable<ScheduledUserReport> {
    return this.http.delete<ScheduledUserReport>(`${environment.serviceUrl}/ad-stats/scheduled-user-reports/${c.id}`);
  }

  /**
   * preset reports
   */
  presetReportsDpLandscape(): Observable<DpLandscapeReport> {
    return this.http.get<DpLandscapeReport>(`${environment.serviceUrl}/ad-stats/preset-reports/dp-landscape-report`);
  }

  presetUnitByDay(dateFrom?: string, dateTo?: string, adUnitIds?: string[], salesRepresentativeEmail?: string, accountManagerEmail?: string): Observable<ByDayReport<UnitByDayReportData>> {
    return this.http.get<ByDayReport<UnitByDayReportData>>(`${environment.serviceUrl}/ad-stats/preset-reports/unit-by-day`, {
      params: HttpUtils.getHttpParams({
        dateFrom,
        dateTo,
        adUnitIds,
        salesRepresentativeEmail,
        accountManagerEmail
      })
    });
  }

  presetImpressionsWidgetSizeByDay(dateFrom?: string, dateTo?: string, salesRepresentativeEmail?: string, accountManagerEmail?: string): Observable<ByDayReport<WidgetSizeByDayReportData>> {
    return this.http.get<ByDayReport<WidgetSizeByDayReportData>>(`${environment.serviceUrl}/ad-stats/preset-reports/widget-size-impressions-by-day`, {
      params: HttpUtils.getHttpParams({
        dateFrom: dateFrom,
        dateTo: dateTo,
        salesRepresentativeEmail,
        accountManagerEmail
      })
    });
  }

  presetWinRate(params: { dateFrom?: string, dateTo?: string, salesRepresentativeEmail?: string, accountManagerEmail?: string, publisherId?: string }): Observable<WinRateReportData[]> {
    return this.http.get<WinRateReportData[]>(`${environment.serviceUrl}/ad-stats/preset-reports/win-rate-report`, {
      params: HttpUtils.getHttpParams(params)
    });
  }

  readyToActive(params?): Observable<SummedMasterReport[]> {
    return this.http.get<SummedMasterReport[]>(`${environment.serviceUrl}/ad-stats/preset-reports/ops-alerts-ready-to-active`);
  }

  negativeProfitabilityUnits(params?: OpsAlertPresetReportFilter): Observable<SummedMasterReport[]> {
    return this.http.post<SummedMasterReport[]>(`${environment.serviceUrl}/ad-stats/preset-reports/ops-alerts-negative-prof-units`, params);
  }

  invalidAdsTxtUnits(params?: OpsAlertPresetReportFilter): Observable<SummedMasterReport[]> {
    return this.http.post<SummedMasterReport[]>(`${environment.serviceUrl}/ad-stats/preset-reports/ops-alerts-invalid-adstxt-units`, params);
  }

  bidderUnits(params?: OpsAlertPresetReportFilter): Observable<SummedMasterReport[]> {
    return this.http.post<SummedMasterReport[]>(`${environment.serviceUrl}/ad-stats/preset-reports/ops-alerts-bidder-units`, params);
  }

  ebdaUnits(params?: OpsAlertPresetReportFilter): Observable<SummedMasterReport[]> {
    return this.http.post<SummedMasterReport[]>(`${environment.serviceUrl}/ad-stats/preset-reports/ops-alerts-eb-units`, params);
  }

  videoUnits(params?: OpsAlertPresetReportFilter): Observable<SummedMasterReport[]> {
    return this.http.post<SummedMasterReport[]>(`${environment.serviceUrl}/ad-stats/preset-reports/ops-alerts-video-units`, params);
  }

  negativeMarginUnits(params?: OpsAlertPresetReportFilter): Observable<SummedMasterReport[]> {
    return this.http.post<SummedMasterReport[]>(`${environment.serviceUrl}/ad-stats/preset-reports/ops-alerts-negative-margin-units`, params);
  }

  unitHbidRevReport(dateFrom: string, dateTo: string) {
    return this.http.get(`${environment.serviceUrl}/ad-stats/preset-reports/unit-hbid-rev-report`, {
      responseType: 'text',
      params: HttpUtils.getHttpParams({
        dateFrom, dateTo
      })
    });
  }

}

export class DpLandscapeReport {
  totalPublishers: number;
  totalSites: number;
  totalUnits: number;
  unitStatuses: { [key: string]: number };
  unitWidgetTypes: { [key: string]: number };
  unitHealthChecks: { [key: string]: number };
  unitTypes: { [key: string]: number };
  unitDpStats: UnitDpStats[];

}

export class UnitDpStats {
  demandPartner: string;
  status: string;
  activityStatus: string;
  count: number;
  countBlank: number;
  countPending: number;
  countActive: number;
}


export class OpsAlertPresetReportFilter {
  dateFrom?: string;
  dateTo?: string;
  salesRepresentativeEmail?: string;
  accountManagerEmail?: string;
  adUnitIds?: string[];
  publisherIds?: string[];
}
